import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../../store/root-reducer';
import { getMyPhoneNumberListAction } from '../../../dialer/extra-actions';
import {
  ResponseErrorWithHandled,
  ResponseSuccess,
} from '../../../../shared/types';
import { sleep } from '../../../../shared/utils/sleep';
import {
  getAccountSubscription,
  getAccountSubscriptionPlanDetail,
  getConnectedUsersAndEmailAccounts,
  getInvoiceUrl,
  getPlans,
  purchaseSubscription,
  applyCouponCode,
  getEmailVerificationCredits,
  getEmailVerificationPlan,
  purchaseEmailVerificationCredits,
  modifySubscription,
  purchaseModifySubscription,
  reactivateSubscription,
  resumeSubscription,
  handleSubscription,
  calculatePay,
  upgradePlan,
  downgradePlan,
  purchaseLtdPlan,
  upgradeLtdPlan,
  modifyLtdPlan,
  getLeadFinderSubscription,
  getLeadFinderPlans,
  purchaseLeadFinderSubscription,
  purchaseLeadFinderModifySubscription,
  handleLeadFinderChurnkeySubscription,
  reactivateLeadFinderSubscription,
  resumeLeadFinderSubscription,
  purchaseHistory,
  purchaseDetailsById,
  exportCreditHistory,
  getLeadFinderOneTimePlans,
  purchaseLeadFinderOneTimePlan,
  dynamicCustomPlans,
  getInboxAnalyzerSubscription,
  purchaseInboxAnalyzerSubscription,
  getInboxAnalyzerPlans,
  purchaseInboxAnalyzerModifySubscription,
  reactivateInboxAnalyzerSubscription,
  resumeInboxAnalyzerSubscription,
  handleInboxAnalyzerChurnkeySubscription,
  modifyInboxAnalyzerSubscription,
  calculateInboxAnalyzerPay,
  getInboxAnalyzerSubscriptionPlanDetail,
  getInboxAnalyzerInvoiceUrl,
  calculateAiCreditsPricings,
  purchaseAiCredits,
  getDefaultAiCreditRate,
  aiCreditHistory,
  schedulePlanUpgrade,
  calculateCreditsPrices,
  purchaseCredits,
  getCreditsTransactionStatus,
  getPaddleSubscription,
  getPaddleSubscriptionMetadata,
  getPaddlePlans,
  previewPaddleSubscription,
  upgradePaddleSubscription,
  cancelPaddleLeadFinderAddon,
  pollPaddleTransactionStatus,
  changePaddleSubscriptionStatus,
  createPaddleDynamicPlan,
  validatePaddleCoupon,
  getDialerPhoneNumberPurchaseHistory,
  reactivateDialerPhoneNumberSubscription,
  exportDialerCreditsUsage,
  createPaddleCustomerPortalSession,
  revokePaddleScheduledPause,
  resumePaddleSubscription,
  reactivatePaddleSubscription,
} from './helpers/billing-subscription.api';
import {
  ApplyCouponCodeRequest,
  GetInvoiceURLRequest,
  PurchaseEmailVerificationCreditsRequest,
  ModifySubscriptionRequest,
  PurchaseSubscriptionRequest,
  PurchaseModifySubscriptionRequest,
  ReactivateResumeSubscriptionRequest,
  HandleSubscriptionRequest,
  CalculatePayRequest,
  UpgradePlanRequest,
  DowngradePlanRequest,
  LTDPurchasePayload,
  LTDModifyPayload,
  LTDUpgradePayload,
} from './types';
import SubscriptionStatus from './enums/subscription-status';
import {
  ExportCreditHistoryPayload,
  GetOrderDetailsByIdPayload,
} from './types/purchase-history';
import {
  DynamicCustomPlansRequest,
  PurchaseLeadFinderCreditsRequest,
} from './types/purchase-lead-finder-credits';
import {
  CalculateAiCreditsPricingsRequest,
  PurchaseAiCreditsRequest,
} from './types/ai-credits';
import {
  CalculateCreditsPricesRequest,
  CreditsTransactionStatusRequest,
  PurchaseCreditsRequest,
} from './types/purchase-credits';
import type {
  PaddlePreviewSubscriptionRequest,
  PaddleUpgradeSubscriptionRequest,
  PaddleTransactionPollingRequest,
  PaddleDynamicPlanRequest,
  PaddleValidateCouponRequest,
  PaddleCustomerPortalRequest,
  PaddlePauseResumeRequest,
  PaddleApplicationType,
} from './types/paddle';
import { resolvePaymentApi } from './helpers/payment-provider';
import {
  getUserSubscriptionMetadataRequest,
  getPostLoadMetaRequest,
  getUserSettingsRequest,
} from '../../../home/extra-actions';
import toaster from '../../../../shared/toaster';

export const getAccountSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getAccountSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddleSubscription('sequence'),
      () => getAccountSubscription(),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getAccountSubscriptionPlanRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('/getAccountSubscriptionPlanRequest', async (args, thunkAPI) => {
  try {
    return await getAccountSubscriptionPlanDetail(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInboxAnalyzerSubscriptionPlanRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('/getInboxAnalyzerSubscriptionPlanRequest', async (args, thunkAPI) => {
  try {
    return await getInboxAnalyzerSubscriptionPlanDetail(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getLeadFinderSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getLeadFinderSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddleSubscription('lead-finder'),
      () => getLeadFinderSubscription(),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInboxAnalyzerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/getInboxAnalyzerSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await resolvePaymentApi(
        thunkAPI.getState,
        () => getPaddleSubscription('inbox-analyzer'),
        () => getInboxAnalyzerSubscription(),
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getPlansRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddlePlans('sequence'),
      () => getPlans(),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getLeadFinderPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getLeadFinderPlansRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddlePlans('lead-finder'),
      () => getLeadFinderPlans(),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInboxAnalyzerPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getInboxAnalyzerPlansRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddlePlans('inbox-analyzer'),
      () => getInboxAnalyzerPlans(),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInvoiceURLRequest = createAsyncThunk<
  ResponseSuccess,
  GetInvoiceURLRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getInvoiceURLRequest', async (args, thunkAPI) => {
  try {
    return await getInvoiceUrl(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInboxAnalyzerInvoiceURLRequest = createAsyncThunk<
  ResponseSuccess,
  GetInvoiceURLRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getInboxAnalyzerInvoiceURLRequest', async (args, thunkAPI) => {
  try {
    return await getInboxAnalyzerInvoiceUrl(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseSubscriptionRequest', async (args, thunkAPI) => {
  try {
    const data = await purchaseSubscription(args);
    await sleep(5000);
    return data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseLeadFinderSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/purchaseLeadFinderSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const data = await purchaseLeadFinderSubscription(args);
      await sleep(5000);
      return data;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const purchaseInboxAnalyzerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/purchaseInboxAnalyzerSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const data = await purchaseInboxAnalyzerSubscription(args);
      await sleep(5000);
      return data;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getConnectedUsersAndEmailAccountsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>(
  'accounts/getConnectedUsersAndEmailAccountsRequest',
  async (args, thunkAPI) => {
    try {
      return await getConnectedUsersAndEmailAccounts();
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const applyCouponCodeRequest = createAsyncThunk<
  ResponseSuccess,
  ApplyCouponCodeRequest & Partial<PaddleValidateCouponRequest>,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/applyCouponCodeRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => validatePaddleCoupon(args as PaddleValidateCouponRequest),
      () => applyCouponCode(args),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getEmailVerificationCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-credits', async (args, thunkAPI) => {
  try {
    return await getEmailVerificationCredits();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getEmailVerificationPlanRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-plans', async (args, thunkAPI) => {
  try {
    return await getEmailVerificationPlan();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseEmailVerificationCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseEmailVerificationCreditsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-credits/purchase', async (args, thunkAPI) => {
  try {
    return await purchaseEmailVerificationCredits(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const modifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ModifySubscriptionRequest & Partial<PaddlePreviewSubscriptionRequest>,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/modifySubscription', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => previewPaddleSubscription(args as PaddlePreviewSubscriptionRequest),
      () => modifySubscription(args),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const modifyInboxAnalyzerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ModifySubscriptionRequest & Partial<PaddlePreviewSubscriptionRequest>,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/modifyInboxAnalyzerSubscription', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => previewPaddleSubscription(args as PaddlePreviewSubscriptionRequest),
      () => modifyInboxAnalyzerSubscription(args),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseModifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseModifySubscriptionRequest & Partial<PaddleUpgradeSubscriptionRequest>,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseModifySubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => upgradePaddleSubscription(args as PaddleUpgradeSubscriptionRequest),
      () => purchaseModifySubscription(args),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseLeadFinderModifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseModifySubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/purchaseLeadFinderModifySubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await resolvePaymentApi(
        thunkAPI.getState,
        () =>
          upgradePaddleSubscription(args as PaddleUpgradeSubscriptionRequest),
        () => purchaseLeadFinderModifySubscription(args),
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const cancelLeadFinderAddonRequest = createAsyncThunk<
  ResponseSuccess,
  { accountSubscriptionPlanId: number },
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/cancelLeadFinderAddonRequest',
  async (args, thunkAPI) => {
    try {
      return await resolvePaymentApi(
        thunkAPI.getState,
        () => cancelPaddleLeadFinderAddon(args),
        () =>
          purchaseLeadFinderModifySubscription({
            accountSubscriptionPlanId: args.accountSubscriptionPlanId,
            slots: 1,
          }),
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const purchaseInboxAnalyzerModifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseModifySubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/purchaseInboxAnalyzerModifySubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await resolvePaymentApi(
        thunkAPI.getState,
        () =>
          upgradePaddleSubscription(args as PaddleUpgradeSubscriptionRequest),
        () => purchaseInboxAnalyzerModifySubscription(args),
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const reactivateSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/reactivateSubscriptionRequest', async (args, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as RootState;
    // Dialer is always billed via Paddle regardless of the account's primary
    // payment provider, so it must never hit the legacy Stripe reactivate API.
    if (args?.application === 'dialer') {
      return await reactivatePaddleSubscription({ application: args?.application });
    }
    if (state.home?.paymentProvider === 'paddle' && args.application) {
      return await reactivatePaddleSubscription({ application: args.application });
    }
    const { application, ...stripeArgs } = args;
    return await reactivateSubscription(stripeArgs);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const reactivateLeadFinderSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/reactivateLeadFinderSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      if (state.home?.paymentProvider === 'paddle' && args.application) {
        return await reactivatePaddleSubscription({ application: args.application });
      }
      const { application, ...stripeArgs } = args;
      return await reactivateLeadFinderSubscription(stripeArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const reactivateInboxAnalyzerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/reactivateInboxAnalyzerSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      if (state.home?.paymentProvider === 'paddle' && args.application) {
        return await reactivatePaddleSubscription({ application: args.application });
      }
      const { application, ...stripeArgs } = args;
      return await reactivateInboxAnalyzerSubscription(stripeArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const resumeSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/resumeSubscriptionRequest', async (args, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as RootState;
    const subscriptionStatus = state.home?.subscription?.status;

    if (state.home?.paymentProvider === 'paddle') {
      if (subscriptionStatus === SubscriptionStatus.PauseScheduled) {
        return await revokePaddleScheduledPause({ application: 'sequence' });
      }
      return await resumePaddleSubscription({ application: 'sequence' });
    }
    const { application, ...stripeArgs } = args;
    return await resumeSubscription(stripeArgs);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const revokePauseSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PaddlePauseResumeRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/revokePauseSubscriptionRequest',
  async (payload, thunkAPI) => {
    try {
      return await revokePaddleScheduledPause(payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const resumeLeadFinderSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/resumeLeadFinderSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const subscriptionStatus = state.home?.leadFinderSubscription?.status;

      if (state.home?.paymentProvider === 'paddle') {
        if (subscriptionStatus === SubscriptionStatus.PauseScheduled) {
          return await revokePaddleScheduledPause({
            application: 'lead-finder',
          });
        }
        return await resumePaddleSubscription({ application: 'lead-finder' });
      }
      const { application, ...stripeArgs } = args;
      return await resumeLeadFinderSubscription(stripeArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const resumeInboxAnalyzerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/resumeInboxAnalyzerSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const subscriptionStatus = state.home?.inboxAnalyzerSubscription?.status;

      if (state.home?.paymentProvider === 'paddle') {
        if (subscriptionStatus === SubscriptionStatus.PauseScheduled) {
          return await revokePaddleScheduledPause({
            application: 'inbox-analyzer',
          });
        }
        return await resumePaddleSubscription({
          application: 'inbox-analyzer',
        });
      }
      const { application, ...stripeArgs } = args;
      return await resumeInboxAnalyzerSubscription(stripeArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const handleSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  HandleSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/handleSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await handleSubscription(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const handleLeadFinderChurnkeySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  HandleSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/handleLeadFinderChurnkeySubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await handleLeadFinderChurnkeySubscription(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const handleInboxAnalyzerChurnkeySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  HandleSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/handleInboxAnalyzerChurnkeySubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await handleInboxAnalyzerChurnkeySubscription(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const calculatePayRequest = createAsyncThunk<
  ResponseSuccess,
  CalculatePayRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/calculatePayRequest', async (args, thunkAPI) => {
  try {
    return await calculatePay(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const calculateInboxAnalyzerPayRequest = createAsyncThunk<
  ResponseSuccess,
  CalculatePayRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/calculateInboxAnalyzerPayRequest', async (args, thunkAPI) => {
  try {
    return await calculateInboxAnalyzerPay(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const upgradePlanRequest = createAsyncThunk<
  ResponseSuccess,
  UpgradePlanRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/upgradePlanRequest', async (args, thunkAPI) => {
  try {
    return await upgradePlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const downgradePlanRequest = createAsyncThunk<
  ResponseSuccess,
  DowngradePlanRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/downgradePlanRequest', async (args, thunkAPI) => {
  try {
    return await downgradePlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDPurchasePayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await purchaseLtdPlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const upgradeLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDUpgradePayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/upgradeLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await upgradeLtdPlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const modifyLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDModifyPayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/modifyLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await modifyLtdPlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPurchaseHistoryRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getPurchaseHistoryRequest', async (args, thunkAPI) => {
  try {
    return await purchaseHistory();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPurchaseDetailsByIdRequest = createAsyncThunk<
  ResponseSuccess,
  GetOrderDetailsByIdPayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getPurchaseDetailsByIdRequest', async (args, thunkAPI) => {
  try {
    return await purchaseDetailsById(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const exportCreditHistoryRequest = createAsyncThunk<
  ResponseSuccess,
  ExportCreditHistoryPayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/exportCreditHistoryRequest', async (args, thunkAPI) => {
  try {
    return await exportCreditHistory(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const aiCreditHistoryRequest = createAsyncThunk<
  ResponseSuccess,
  ExportCreditHistoryPayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/aiCreditHistoryRequest', async (args, thunkAPI) => {
  try {
    return await aiCreditHistory(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getLeadFinderOneTimePlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getLeadFinderOneTimePlansRequest', async (args, thunkAPI) => {
  try {
    return await getLeadFinderOneTimePlans();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseLeadFinderOneTimePlanRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseLeadFinderCreditsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseLeadFinderPlanRequest', async (args, thunkAPI) => {
  try {
    return await purchaseLeadFinderOneTimePlan(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const dynamicCustomPlansRequest = createAsyncThunk<
  ResponseSuccess,
  DynamicCustomPlansRequest & PaddleDynamicPlanRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/dynamicCustomPlansRequest', async (args, thunkAPI) => {
  try {
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => createPaddleDynamicPlan(args),
      () => dynamicCustomPlans(args),
    );
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const calculateAiCreditsPricingsRequest = createAsyncThunk<
  ResponseSuccess,
  CalculateAiCreditsPricingsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/calculateAiCreditsPricingsRequest', async (args, thunkAPI) => {
  try {
    return await calculateAiCreditsPricings(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseAiCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseAiCreditsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseAiCreditsRequest', async (args, thunkAPI) => {
  try {
    return await purchaseAiCredits(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

/**
 * Get Default Credit Rate Request
 */
export const getDefaultAiCreditRateRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getDefaultCreditRateRequest', async (args, thunkAPI) => {
  try {
    return await getDefaultAiCreditRate();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const schedulePlanUpgradeRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/schedulePlanUpgradeRequest', async (_args, thunkAPI) => {
  try {
    return await schedulePlanUpgrade();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const calculateCreditsPricesRequest = createAsyncThunk<
  ResponseSuccess,
  CalculateCreditsPricesRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/calculateCreditsPricesRequest', async (args, thunkAPI) => {
  try {
    return await calculateCreditsPrices(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseCreditsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseCreditsRequest', async (args, thunkAPI) => {
  try {
    return await purchaseCredits(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getCreditsTransactionStatusRequest = createAsyncThunk<
  ResponseSuccess,
  CreditsTransactionStatusRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getCreditsTransactionStatusRequest', async (args, thunkAPI) => {
  try {
    return await getCreditsTransactionStatus(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// PADDLE-ONLY THUNKS (no Stripe equivalent)
// ─────────────────────────────────────────────────────────────────────────────

export const getPaddleSubscriptionMetadataRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('billingSubscription/getPaddleSubscriptionMetadata', async (_, thunkAPI) => {
  try {
    const { data } = (await getPaddleSubscriptionMetadata()) as any;
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const pollPaddleTransactionStatusRequest = createAsyncThunk<
  ResponseSuccess,
  PaddleTransactionPollingRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingSubscription/pollPaddleTransactionStatus',
  async (payload, thunkAPI) => {
    try {
      const data = (await pollPaddleTransactionStatus(payload)) as any;
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createPaddleCustomerPortalSessionRequest = createAsyncThunk<
  ResponseSuccess,
  PaddleCustomerPortalRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingSubscription/createPaddleCustomerPortalSession',
  async (payload, thunkAPI) => {
    try {
      const { data } = (await createPaddleCustomerPortalSession(
        payload,
      )) as any;
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const revokePaddleScheduledPauseRequest = createAsyncThunk<
  ResponseSuccess,
  PaddlePauseResumeRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingSubscription/revokePaddleScheduledPause',
  async (payload, thunkAPI) => {
    try {
      const { data } = await revokePaddleScheduledPause(payload);
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getDialerSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getDialerSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await getPaddleSubscription('dialer');
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// PADDLE POLLING THUNK (centralized polling manager)
// ─────────────────────────────────────────────────────────────────────────────

const PADDLE_POLLING_INTERVAL_MS = 2000;
const PADDLE_MAX_POLLING_ATTEMPTS = 60;

// Use inline action types to avoid circular dependency with billing-subscription-slice
const SLICE_PREFIX = 'billingSubscription';

export const paddlePollingThunk = createAsyncThunk<
  void,
  {
    shAccountId: number;
    applicationType: PaddleApplicationType;
    planId?: number;
    dialerCartId?: number;
  },
  { rejectValue: ResponseErrorWithHandled; state: any }
>(
  'billingSubscription/paddlePollingThunk',
  async ({ shAccountId, applicationType, planId, dialerCartId }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;

    // Clear any existing polling first (singleton guarantee)
    dispatch({ type: `${SLICE_PREFIX}/stopPaddlePolling` });

    // Start fresh
    dispatch({ type: `${SLICE_PREFIX}/startPaddlePolling`, payload: applicationType });

    // Call change-status API (fire-and-forget — polling must never be blocked)
    changePaddleSubscriptionStatus({
      shAccountId,
      applicationType,
      planId,
    }).catch(() => {
      // Intentionally swallowed — polling continues regardless
    });

    const poll = (): Promise<void> =>
      new Promise((resolve) => {
        const executePoll = async () => {
          const state = getState();
          const pollingState = state.billingAndSubscription.paddlePolling;

          // Guard: stop if polling was cancelled externally
          if (!pollingState.isActive) {
            resolve();
            return;
          }

          dispatch({ type: `${SLICE_PREFIX}/incrementPaddlePollingCount` });

          const currentCount = pollingState.pollingCount + 1;

          if (currentCount > PADDLE_MAX_POLLING_ATTEMPTS) {
            dispatch({ type: `${SLICE_PREFIX}/setPaddlePollingStatus`, payload: 'timeout' });
            toaster.error(
              'Payment verification timed out after 2 minutes. Please check your subscription status.',
            );
            resolve();
            return;
          }

          try {
            const response = await pollPaddleTransactionStatus({
              shAccountId,
              applicationType,
              ...(dialerCartId && { dialerCartId }),
            });

            // Re-check active state after async call
            const currentState = getState();
            if (!currentState.billingAndSubscription.paddlePolling.isActive) {
              resolve();
              return;
            }

            const shouldPoll = (response as any)?.payload?.shouldPoll;

            if (shouldPoll === false) {
              // Success: clear polling state BEFORE dispatching success APIs
              dispatch({ type: `${SLICE_PREFIX}/setPaddlePollingStatus`, payload: 'succeeded' });

              const successMessage =
                applicationType === 'dialer-phone-number'
                  ? 'Phone numbers have been purchased successfully'
                  : 'Your subscription has been activated successfully!';
              toaster.success(successMessage);

              // Dispatch success APIs based on applicationType
              switch (applicationType) {
                case 'sequence':
                  dispatch(getAccountSubscriptionRequest());
                  break;
                case 'lead-finder':
                  dispatch(getLeadFinderSubscriptionRequest());
                  break;
                case 'inbox-analyzer':
                  dispatch(getInboxAnalyzerSubscriptionRequest());
                  break;
                case 'dialer':
                  dispatch(getDialerSubscriptionRequest());
                  break;
                case 'dialer-phone-number':
                  dispatch(getDialerSubscriptionRequest());
                  dispatch(getMyPhoneNumberListAction({}));
                  break;
                default:
                  break;
              }

              dispatch(getUserSubscriptionMetadataRequest());
              dispatch(getPostLoadMetaRequest());
              dispatch(getUserSettingsRequest());

              resolve();
              return;
            }
          } catch {
            const currentState = getState();
            if (!currentState.billingAndSubscription.paddlePolling.isActive) {
              resolve();
              return;
            }

            dispatch({ type: `${SLICE_PREFIX}/setPaddlePollingStatus`, payload: 'failed' });
            toaster.error(
              'Something went wrong while verifying your payment. Please check your subscription status.',
            );
            resolve();
            return;
          }

          // Schedule next poll
          setTimeout(executePoll, PADDLE_POLLING_INTERVAL_MS);
        };

        setTimeout(executePoll, PADDLE_POLLING_INTERVAL_MS);
      });

    await poll();
  },
);

export const getDialerPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getDialerPlansRequest', async (args, thunkAPI) => {
  try {
    return await getPaddlePlans('dialer');
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseDialerModifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PaddleUpgradeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/purchaseDialerModifySubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await upgradePaddleSubscription(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getDialerPhoneNumberPurchaseHistoryRequest = createAsyncThunk<
  ResponseSuccess,
  { pageNumber: number; pageSize: number },
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/getDialerPhoneNumberPurchaseHistoryRequest',
  async (args, thunkAPI) => {
    try {
      return await getDialerPhoneNumberPurchaseHistory(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const exportDialerCreditsUsageRequest = createAsyncThunk<
  ResponseSuccess,
  { startDate: string; endDate: string },
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/exportDialerCreditsUsageRequest',
  async (args, thunkAPI) => {
    try {
      return await exportDialerCreditsUsage(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const reactivateDialerPhoneNumberSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  { paddleSubscriptionId: string },
  { rejectValue: ResponseErrorWithHandled }
>(
  'subscription/reactivateDialerPhoneNumberSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await reactivateDialerPhoneNumberSubscription(args);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);
